import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/ui/PasswordInput";
import { toast } from "sonner";
import { MessageCircle, Mail, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, loginAdmin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isAdminMode = params.get("admin") === "1";
  const [mode, setMode] = useState(isAdminMode ? "email" : "whatsapp");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = mode === "whatsapp"
        ? await login(whatsapp, password)
        : await loginAdmin(email, password);
      toast.success("¡Bienvenido!");
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "customer") navigate("/mi-cuenta");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="overline text-[hsl(var(--primary))]">
          {isAdminMode ? "Acceso Super Admin" : "Acceso comercios"}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">
          {isAdminMode ? (
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#5B21B6]" /> Login Admin
            </span>
          ) : "Iniciar sesión"}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {isAdminMode
            ? "Ingresa con tu correo y contraseña de administrador."
            : "Entra a tu panel para gestionar tu anuncio y la lealtad de tus clientes."}
        </p>

        <div className="mt-6 flex bg-white border border-border rounded-full p-1 text-sm" data-testid="login-mode-toggle">
          <button
            type="button"
            onClick={() => setMode("whatsapp")}
            className={`flex-1 rounded-full py-2 px-3 flex items-center justify-center gap-1.5 transition-colors ${mode === "whatsapp" ? "bg-[#7C3AED] text-white" : "text-zinc-600 hover:text-zinc-900"}`}
            data-testid="login-mode-whatsapp"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
          <button
            type="button"
            onClick={() => setMode("email")}
            className={`flex-1 rounded-full py-2 px-3 flex items-center justify-center gap-1.5 transition-colors ${mode === "email" ? "bg-[#7C3AED] text-white" : "text-zinc-600 hover:text-zinc-900"}`}
            data-testid="login-mode-email"
          >
            <Mail className="w-4 h-4" /> Correo
          </button>
        </div>

        <form onSubmit={submit} className="mt-4 space-y-4 bg-white border border-border rounded-2xl p-4 sm:p-6" data-testid="login-form">
          {mode === "whatsapp" ? (
            <div>
              <Label htmlFor="whatsapp">WhatsApp (10 dígitos)</Label>
              <Input
                id="whatsapp"
                type="tel"
                inputMode="numeric"
                required
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="5512345678"
                className="mt-1.5"
                data-testid="login-whatsapp-input"
              />
              {whatsapp.length > 0 && whatsapp.length < 10 && (
                <p className="text-[11px] text-amber-600 mt-1">{whatsapp.length}/10 dígitos — faltan {10 - whatsapp.length}</p>
              )}
              {whatsapp.length === 10 && (
                <p className="text-[11px] text-green-600 mt-1">✓ Número completo</p>
              )}
            </div>
          ) : (
            <div>
              <Label htmlFor="email">Correo electrónico</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="login-email-input" />
              <div className="text-[11px] text-muted-foreground mt-1">
                Solo si te registraste con correo (admin o comercios antiguos).
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="password">Contraseña</Label>
            <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="login-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full btn-primary rounded-full py-6" data-testid="login-submit-btn">
            {busy ? "Entrando…" : "Entrar"}
          </Button>
          {mode === "whatsapp" && (
            <div className="text-sm text-center">
              <Link to="/recuperar-contrasena?rol=business" className="text-muted-foreground hover:text-zinc-800 hover:underline" data-testid="login-forgot-password">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          )}
          <div className="text-sm text-center text-muted-foreground">
            ¿No tienes cuenta?{" "}
            <Link to="/registro" className="text-[hsl(var(--primary))] font-medium">Afilia tu negocio</Link>
          </div>
          <div className="text-xs text-center text-muted-foreground pt-2 border-t border-border">
            ¿Eres cliente? <Link to="/login-cliente" className="text-[#5B21B6] hover:underline font-medium">Inicia sesión aquí</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
